import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import fireConfetti from "@/lib/fireConfetti";

export default function FestivalThemesSection() {
  const { activeWorkspaceId } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [manualApplied, setManualApplied] = useState(null);
  const [tab, setTab] = useState("festivals");

  const load = async () => {
    if (!activeWorkspaceId) return;
    try {
      const [{ data }, { data: active }] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/festival-themes`),
        api.get(`/workspaces/${activeWorkspaceId}/festival-themes/active`).catch(() => ({ data: null })),
      ]);
      setCatalog(data.catalog || []);
      setManualApplied(active?.source === "manual" ? active.active : null);
    } catch { /* ok */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  useEffect(() => {
    if (!previewing) return;
    const root = document.documentElement;
    const oldAccent = root.style.getPropertyValue("--primary");
    if (previewing.accent_hsl) root.style.setProperty("--primary", previewing.accent_hsl);
    const t = setTimeout(() => setPreviewing(null), 20000);
    return () => {
      clearTimeout(t);
      if (oldAccent) root.style.setProperty("--primary", oldAccent);
      else root.style.removeProperty("--primary");
    };
  }, [previewing]);

  const toggle = async (t) => {
    setBusyId(t.id);
    try {
      if (t.imported && t.enabled) {
        await api.delete(`/workspaces/${activeWorkspaceId}/festival-themes/${t.id}`);
        toast.success("Theme disabled");
      } else {
        await api.post(`/workspaces/${activeWorkspaceId}/festival-themes/${t.id}/import`, { enabled: true });
        toast.success(t.imported ? "Theme re-enabled" : `Imported ${t.name}`);
      }
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusyId(null); }
  };

  const applyNow = async (t, hours = 24) => {
    setBusyId(t.id);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/festival-themes/manual-override`, {
        theme_id: t.id, duration_hours: hours,
      });
      toast.success(`Applied "${t.name}" for the next ${hours}h`);
      if (t.effect === "confetti") fireConfetti();
      window.dispatchEvent(new CustomEvent("festival:refresh"));
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
    finally { setBusyId(null); }
  };
  const clearOverride = async () => {
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/festival-themes/manual-override`, {
        theme_id: null,
      });
      toast.success("Theme cleared");
      window.dispatchEvent(new CustomEvent("festival:refresh"));
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Clear failed")); }
  };

  const festivals = catalog.filter((c) => c.kind !== "fun");
  const funs = catalog.filter((c) => c.kind === "fun");
  const shown = tab === "festivals" ? festivals : funs;

  return (
    <div className="border border-border p-6 space-y-4" data-testid="settings-festival-themes">
      <div>
        <div className="label-eyebrow flex items-center gap-2">
          <PartyPopper className="w-4 h-4 text-primary" />
          Festival & special-day themes
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Preview a theme instantly, apply it on demand for the day, or import a festival so it auto-activates during its date range.
          Fun themes (like Fun Friday) never auto-activate — apply them yourself when the mood strikes.
        </p>
      </div>

      {manualApplied && (
        <div className="border border-primary/40 bg-primary/5 p-3 flex items-center gap-2 text-sm" data-testid="manual-override-banner">
          <span className="text-lg">{manualApplied.emoji}</span>
          <div className="flex-1">
            <strong>{manualApplied.name}</strong> is currently applied workspace-wide.
          </div>
          <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={clearOverride} data-testid="manual-override-clear-btn">
            Clear
          </Button>
        </div>
      )}

      <div className="flex gap-0 border-b border-border -mx-6 px-6" data-testid="festival-tabs">
        <button type="button" onClick={() => setTab("festivals")}
          className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px ${tab === "festivals" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="festival-tab-festivals">
          Festivals ({festivals.length})
        </button>
        <button type="button" onClick={() => setTab("fun")}
          className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px ${tab === "fun" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="festival-tab-fun">
          Fun themes ({funs.length})
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {shown.map((t) => (
          <div key={t.id} className="border border-border p-3 flex gap-3 items-start" data-testid={`festival-theme-${t.id}`}>
            <div className="text-2xl">{t.emoji}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{t.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                {t.kind === "fun" ? "On-demand" : t.region}
                {t.kind !== "fun" && t.date_ranges?.length ? ` · ${(t.date_ranges || []).map((r) => r.join("→")).join(", ")}` : ""}
              </div>
              <p className="text-xs text-muted-foreground">{t.description}</p>
              <div className="mt-2 h-2 w-full" style={{ background: t.banner_gradient }} />
              <div className="mt-2 flex flex-wrap gap-1">
                <button type="button" onClick={() => setPreviewing(t)}
                  className="text-[11px] px-2 py-0.5 border border-border hover:bg-muted rounded-none"
                  data-testid={`festival-preview-${t.id}`}>
                  Preview (20s)
                </button>
                <button type="button" onClick={() => applyNow(t, 24)} disabled={busyId === t.id}
                  className="text-[11px] px-2 py-0.5 border border-primary bg-primary/10 text-primary hover:bg-primary/20 rounded-none"
                  data-testid={`festival-apply-${t.id}`}>
                  Apply for 24h
                </button>
                {t.kind !== "fun" && (
                  <button onClick={() => toggle(t)} disabled={busyId === t.id}
                    className={`text-[11px] px-2 py-0.5 border rounded-none ${t.imported && t.enabled ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100" : "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
                    data-testid={`festival-toggle-${t.id}`}>
                    {busyId === t.id ? "…" : (t.imported && t.enabled ? "Disable auto" : "Import (auto)")}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {previewing && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 shadow-lg border border-border"
             style={{ background: previewing.banner_gradient, color: previewing.banner_text_color }}
             data-testid="theme-preview-toast">
          <span className="text-lg">{previewing.emoji}</span>
          <span className="font-semibold text-sm">Previewing &quot;{previewing.name}&quot;</span>
          <button type="button" onClick={() => setPreviewing(null)}
                  className="ml-2 underline text-xs opacity-90 hover:opacity-100"
                  data-testid="theme-preview-close">
            Stop
          </button>
        </div>
      )}
    </div>
  );
}
